import { VoyageDialogPopupManager } from "../../local_modules/voyage-dialog-popup/src/voyage-dialog-popup-manager";
import { WeatherRoutingService } from "../services/weather-routing-service";
import { checkIfPilotEtdIsLessThanCurrentTime, formatDate, getWrPopupContent, getWrPopupContentForOriginalPlan } from "../utils/utils";
import { VesselPlanningModel, VoyagePlan } from "./vessel-planning-model";
import { VoyageBestPracticeRoutesModel } from "./voyage-best-practice-routes-model";
import { VoyageRoute, WeatherRoutingInfo } from "./voyage-routing-model";
import { VoyageWeatherRoutingModelBase } from "./weather-routing-model";
import { AlertType, VesselAlertsModel } from "./vessel-alerts-model";
import { VesselNotificationsService } from "../services/vessel-notifications-service";
import { NotificationTypes, WRMessageTypes } from "../services/types/vessel-notifications-services.types";
import { VoyageToastManager } from "../../local_modules/voy-toast/src/voy-toast-manager";
import { mergeWrInfoForStatusUpdate, WRStatus } from "../../local_modules/voyage-suggested-routes/src/helper";
import { VesselLoaderManager } from "../../local_modules/vessel-loader/src/vessel-loader-manager";
import { WeatherRoutingModel } from "./weather-routing-reports-model";


const THIRTY_MINUTES = 30 * 60 * 1000;
const ONE_HOUR = 60 * 60 * 1000;
const SIXTY_HOURS = 60 * 60 * 1000 * 60;
export const ACCEPT_SHOW_PLAN = 'accept-show-plan';
const getWRActionNeededAlertId = (scheduleId: string) => `weather-routing-advice-action-needed-${scheduleId}`;

export type SafetyChecks = {
    isPassed: boolean;
    eta: {
        isPassed: boolean;
        value: string;
    } | null;
    performance: {
        isPassed: boolean;
        voyage_length_limit: string;
        voyage_fuel_min_limit: string;
        voyage_fuel_max_limit: string;
        voyage_length_value: string;
        voyage_fuel_value: string;
    } | null;
    parametricRoll: {
        isPassed: boolean;
        duration: string;
        occurrence: string;
    } | null;
    heavyWeather: Array<{
        key: string;
        isPassed: boolean;
        limit: string;
        value: string;
        unit: string;
    }> | null;
};

export type UnReadWRAdvice = {
    id: number;
    safety_checks?: SafetyChecks;
    schedule_id: string;
    wni_plan_type: string;
}

export type UnReadWRAdviceResponse = UnReadWRAdvice[];

export class VoyageWeatherRoutingModelImpl extends VoyageWeatherRoutingModelBase {
    
    constructor() {
        super();

        this.checkWrAvailability = this.checkWrAvailability.bind(this);
        this.getWRAdvice = this.getWRAdvice.bind(this);
        this.getWRStatus = this.getWRStatus.bind(this);
        this.updateWRStatus = this.updateWRStatus.bind(this);
        this.dialogShowPlan = this.dialogShowPlan.bind(this);
        this.handleAccept = this.handleAccept.bind(this);
        this.checkIfWrAdviceNeedsAction = this.checkIfWrAdviceNeedsAction.bind(this);

        // call the function to check the availability of the WR advice every 5 seconds
        setInterval(this.checkWrAvailability, THIRTY_MINUTES);
        VesselPlanningModel!.addEventListener('view-wni-advice-clicked-from-notification', (e: any) => this.handleAccept(e.detail.wrId, e.detail.scheduleId));
        setInterval(this.checkIfOriginalPlanApproved, THIRTY_MINUTES);
        setInterval(this.checkIfWrAdviceNeedsAction, THIRTY_MINUTES);
        
        this.checkWrAvailability();
        this.checkIfOriginalPlanApproved();
        this.checkIfWrAdviceNeedsAction();
    }


    // Map<scheduleId, WRStatus>
    private _wrStatusByScheduleId: Map<string, Object> = new Map();
    private _isPopupShown: boolean = false;
    private _isNotificationShown: boolean = false;
    private _isOriginalPlanApprovedPopupShown: boolean = false;

    getWrStatusByScheduleId(scheduleId: string): boolean {
        const wrStatus: any =  this._wrStatusByScheduleId.get(scheduleId);
        if(!wrStatus) return false;
        return wrStatus?.weather_routing_status ?? false;
    }


    async checkIfOriginalPlanApproved(): Promise<any> {
        
        const response = await VesselNotificationsService.fetchActiveNotifications(NotificationTypes.WNIOriginalPlan);

        if(response.length > 0 && !this._isOriginalPlanApprovedPopupShown){
            const notification = response[0];
            const notificationData = notification.data;
            if(!notification && notification.type !== NotificationTypes.WNIOriginalPlan) return;
            //get the recent plan fron BE
            this._isOriginalPlanApprovedPopupShown = true;
            VoyageDialogPopupManager.createDialog({
                headerText: "Route Approved",
                bodyText: `Your suggested route has been reviewed and accepted by WNI.`,
                acceptButtonText: "Ok",
                noCloseButton: true
            }).getResponse().then(async (response) => {
                if(response === "accept"){
                    const plans = await VesselPlanningModel!.getPlans(notificationData.scheduleId);
                    const plan = plans[0];
                    if(plan) {
                        if(checkIfPilotEtdIsLessThanCurrentTime(plan.departurePilotEtd)) {
                            const shouldCreateDepartureReportNotification = await WeatherRoutingModel!.shouldCreateDepartureReportNotification();
                            if(shouldCreateDepartureReportNotification) {
                                const departureReport = await WeatherRoutingModel!.checkIfReportExists(plan.scheduleId, 'Departure');
                                if(!departureReport?.isSubmitted) {
                                    VesselNotificationsService.createWniReportNotification(
                                        AlertType.WNIReports,
                                        plan.scheduleId,
                                        'Departure'
                                    );
                                }
                            }
                        }
                    } 
                    await VesselNotificationsService.closeNotification(notification.id);
                    this._isOriginalPlanApprovedPopupShown = false;
                }
            })
        }
    }

    async checkWrAvailability(): Promise<any> {
        if (this._isPopupShown) return;
    
        try {
            const response: UnReadWRAdviceResponse = await WeatherRoutingService.getWrAdviceAvailibilty();
            if (!response) return; 

            
            const plans = await VesselPlanningModel!.getPlans(response[0].schedule_id);
            if (!plans[0] || !plans[0].status || plans[0].status !== 'approved') {
                console.warn(`No approved plan found for the schedule id: ${response[0].schedule_id}`);
                return;
            }

            const SnoozedWr = JSON.parse(localStorage.getItem('SnoozedWr') || '{}');
        
            if (SnoozedWr) {
                const elapsedTime = this.getElapsedTimeMs(SnoozedWr.timeStamp);
                if (elapsedTime < ONE_HOUR) {
                    if (!this._isNotificationShown) {
                        this.createAlert(response[0].schedule_id);
                    }
                    return;
                }
            }
    
            // Proceed with showing the dialog if weather advice is available
            if (response[0].wni_plan_type === 'Original') {
                this.originalPlanApprovalDialog(plans[0], response[0]);
            } else {
                this.dialogShowPlan(plans[0], response[0]);
            }

            VesselAlertsModel!.removeAlert((<any>window)["appenv"].vesselIMONumber, 'weather-routing-advice-available');
            this._isNotificationShown = false;
            return;
        } catch (error) {
            console.error("Error in fetching Weather Routing Advice:", error);
            return null;
        }
    }
    
    async getWRAdvice(id: string): Promise<VoyageRoute | null> {
        try {
            return await WeatherRoutingService.getWRAdvice(id);
        } catch (error) {
            console.warn("There has been a problem with your fetch operation:", error);
            return null;
        }
    }

    private async originalPlanApprovalDialog(plan: VoyagePlan, wrAdvice: UnReadWRAdvice) {

        const wrAdvicePopUp = VoyageDialogPopupManager.createDialog({
            headerText: "Route Approved By Wni",
            bodyContent: getWrPopupContentForOriginalPlan(wrAdvice),
            acceptButtonText: "View Pdf",
            noCloseButton: true
        });

        this._isPopupShown = true;
        const response = await wrAdvicePopUp.getResponse();

        if (response === "accept") {
            const pdf = await WeatherRoutingService.getWRPdf(wrAdvice.id.toString());
            if(pdf) {
                const blob = new Blob([pdf], { type: 'application/pdf' });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `wni-${plan.departureTerminalCode}-${plan.arrivalTerminalCode}-${formatDate(new Date().toString())}pdf`;
                a.click();
                window.URL.revokeObjectURL(url);
            }
        }

        this.dispatchEvent(new CustomEvent('show-wni-pdf', { detail: { showAdvice:true ,wrId: wrAdvice.id.toString() } }));


        await this.updateWRStatus(
            wrAdvice.id.toString(),
            WRStatus.Read,
            mergeWrInfoForStatusUpdate(plan, plan.routes[plan.selectedRouteIndex].weatherRoutingInfo),
        );

        this._isPopupShown = false;
    }
    
    private async dialogShowPlan(plan: VoyagePlan, wrAdvice: UnReadWRAdvice) {
        const userCreatedRoute = this.getUserCreatedRoute(plan);

        const isWeatherRouteUpdate = plan?.routes.find(r => r.routeCreatedBy === 'WR' && r?.weatherRoutingInfo?.status === WRStatus.Accept);

        const headerText = isWeatherRouteUpdate ? `Weather Advice Update - ${formatDate(new Date().toString())}` : `New Weather Advice - ${formatDate(new Date().toString())}`;
        const acceptButtonText = isWeatherRouteUpdate ? "View Update" : "View Advice";
    
        const wrAdvicePopUp = VoyageDialogPopupManager.createDialog({
            headerText: headerText,
            bodyContent: getWrPopupContent(wrAdvice, plan.routes.length >= 5 ? userCreatedRoute?.name : undefined, !!isWeatherRouteUpdate),
            acceptButtonText: acceptButtonText,
            rejectButtonText: "Review Later",
            noCloseButton: true
        });
    
        this._isPopupShown = true;
        const response = await wrAdvicePopUp.getResponse();
    
        if (response === "accept") {
            await this.handleAccept(wrAdvice?.id.toString(), plan.scheduleId);
        } else if (response === "reject") {
            VoyageToastManager.createToast({ message: WRMessageTypes.ReviewLater, type: 'success' });
            this.handleReject(wrAdvice?.id.toString(), plan.scheduleId);
        }
        
        this._isPopupShown = false;
    }
    
    private async handleAccept(id: string, scheduleId: string) {
        try {
            VesselLoaderManager.createLoader(
                {
                    headerText: "Downloading Weather Routing Advice",
                    showCancelButton: true,
                    onCancel: () => {
                        console.log("Canceled downloading weather routing advice");
                    }
                }
            );
            if(!scheduleId) return;

            const plans = await VesselPlanningModel!.getPlans(scheduleId);
            if (!plans[0] || !plans[0].status || plans[0].status !== 'approved') {
                console.warn("No executing plan found.");
                return;
            }

            const plan = plans[0];

            const userCreatedRoute = this.getUserCreatedRoute(plan);
            VoyageBestPracticeRoutesModel!.getRankedBPRsForSchedule(plan);

            const UnReadWRAdvice = await WeatherRoutingService.getWrAdviceAvailibilty();

            if(!UnReadWRAdvice) {
                this.dispatchEvent(new CustomEvent(ACCEPT_SHOW_PLAN, { detail: { scheduleId: plan.scheduleId } }));
                console.warn("No UnReadWRAdvice found.");
                return;
            };
    
            const res = await this.getWRAdvice(id);
            if (!res) return;

            // If plan has more than 5 routes, remove user-created route
            if (plan.routes.length === 5 && userCreatedRoute) {
                const index = plan.routes.findIndex(r => r.routeId === userCreatedRoute.routeId);
                plan.routes.splice(index, 1);
            }
            // check if there is any non selected wr route, if yes, remove it
            const nonSelectedWrRoute = plan.routes.find(r => r.routeCreatedBy === 'WR' && r.routeId !== plan.routes[plan.selectedRouteIndex].routeId);
            
            if(nonSelectedWrRoute) {
                const index = plan.routes.findIndex(r => r.routeId === nonSelectedWrRoute.routeId);
                plan.routes.splice(index, 1);
            }

            if (res.weatherRoutingInfo) {
                res.weatherRoutingInfo = mergeWrInfoForStatusUpdate(plan, {
                    ...res.weatherRoutingInfo,
                    status: WRStatus.Read,
                });
            }

            // update selected route index's weatherRoutingInfo updatedReason to 'Regular'
            if(plan.routes[plan.selectedRouteIndex]?.weatherRoutingInfo) {
                plan.routes[plan.selectedRouteIndex].weatherRoutingInfo!.updated_at_reason = 'Regular';
                plans[0].routes[plans[0].selectedRouteIndex].weatherRoutingInfo!.updated_at_reason_type = 'Manual';
            }
            
            plan.routes.push(res);
            await VesselPlanningModel!.setPlan(plan.scheduleId, plan, false);
            this.updateWRStatus(id, WRStatus.Read, res.weatherRoutingInfo!);

            // download pdf automatically with name wni-from_port-to_port-dateTime.pdf
            const pdf = await WeatherRoutingService.getWRPdf(id);
            if(pdf) {
                const blob = new Blob([pdf], { type: 'application/pdf' });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                const dateTime = new Date().toString();
                const formattedDateTime = formatDate(dateTime);
                a.href = url;
                a.download = `wni-${plan.departureTerminalCode}-${plan.arrivalTerminalCode}-${formattedDateTime}pdf`;
                a.click();
                window.URL.revokeObjectURL(url);
            }
            
            this.dispatchEvent(new CustomEvent(ACCEPT_SHOW_PLAN, { detail: { scheduleId: plan.scheduleId } }));
            localStorage.removeItem('SnoozedWr');
        } catch (error) {
            console.error("Error handling accept action:", error);
        } finally {
                VesselLoaderManager.removeLoader();
        }
    }
    
    private handleReject(id: string, scheduleId: string) {
        const SnoozedWr = {
            id,
            scheduleId,
            timeStamp: new Date().toString()
        }
        localStorage.setItem('SnoozedWr', JSON.stringify(SnoozedWr));
        this.createAlert(scheduleId);
    }
    
    private getElapsedTimeMs(timestamp: string): number {
        return new Date().getTime() - new Date(timestamp).getTime();
    }
    
    private getUserCreatedRoute(plan: VoyagePlan): VoyageRoute | undefined {
        return plan.routes
            .map((r, index) => ({ ...r, index }))
            .sort((a, b) => b.index - a.index)
            .find(r => r.routeCreatedBy !== 'system' && r.routeCreatedBy !== 'WR');
    }
    
    private async createAlert(scheduleId: string) {
        const plan = await VesselPlanningModel!.getCurrentPlan(scheduleId);
        if (!plan) {
            console.warn("No plan found.");
            return;
        }
        const isWeatherRouteUpdate = plan?.routes.find(r => r.routeCreatedBy === 'WR' && r?.weatherRoutingInfo?.status === WRStatus.Accept);
        const message = isWeatherRouteUpdate ? 'A new Weather Update has been received, please review it and take action.' : 'A new Weather Advice has been received, please review it and take action.';
        VesselAlertsModel!.addAlert((<any>window)["appenv"].vesselIMONumber, { 
            type: AlertType.WeatherRoutingAdviceAvailable,
            message: message,
            id: 'weather-routing-advice-available',
        });
        this._isNotificationShown = true;
    }

    async getWRStatus(departureTerminalCode: string, arrivalTerminalCode: string, scheduleId: string): Promise<any> {
        try {
            const wrStatus = await WeatherRoutingService.getWRStatus(departureTerminalCode, arrivalTerminalCode, scheduleId);
            if(this._wrStatusByScheduleId.has(scheduleId)){
                this._wrStatusByScheduleId.delete(scheduleId);
            }
            this._wrStatusByScheduleId.set(scheduleId, wrStatus);

            return wrStatus;
        } catch (error) {
            console.warn("There has been a problem with your fetch operation:", error);
            return null;
        }
    }

    async updateWRStatus(WrId: string, status: WRStatus, weatherRoutingInfo: WeatherRoutingInfo): Promise<any> {
        try {
            const response = await WeatherRoutingService.updateWRStatus(WrId, status, weatherRoutingInfo);
            return response;
        } catch (error) {
            console.warn("There has been a problem updating WR status:", error);
            return null;
        }
    }

    async getWRPdf(id: string): Promise<any> {
        try {
            const pdf = await WeatherRoutingService.getWRPdf(id);
            return pdf;
        } catch (error) {
            console.warn("There has been a problem with your fetch operation:", error);
            return null;
        }
    }

    async checkIfWrAdviceNeedsAction() {
        try {
            const plans = await VesselPlanningModel!.getAllPlans();
            // Filter plans - only executing plan & future plan which has etd less than 60hrs
            const desiredPlans = plans.filter(plan => {
                const etd = new Date(plan.etd).getTime();
                const currentTime = new Date().getTime();
                const eta = new Date(plan.eta).getTime();
                return plan.status === 'approved' && (etd - currentTime) < SIXTY_HOURS && (eta - currentTime) > 0;
            })

            desiredPlans.map((plan)=> {
                if (!plan || !plan.routes || plan.routes.length === 0) {
                    console.warn("No plan or routes found for the given schedule ID.", plan.scheduleId);
                    return false;
                }
    
                const WrAdviseNeedsAction = plan.routes.some(route =>
                    route.routeCreatedBy === 'WR' &&
                    (route.weatherRoutingInfo?.status === WRStatus.Read || route.weatherRoutingInfo?.status === WRStatus.UnRead)
                );
    
                // create alert if WR advice needs action
                if (WrAdviseNeedsAction) {
                    const message = `please review the Weather Routing Advice and take action for ${plan.departureTerminalCode} to ${plan.arrivalTerminalCode} voyage.`;
                    VesselAlertsModel!.addAlert((<any>window)["appenv"].vesselIMONumber, { 
                        type: AlertType.WeatherRoutingAdviceActionNeeded,
                        message: message,
                        id: getWRActionNeededAlertId(plan.scheduleId),
                        scheduleId: plan.scheduleId,
                    });
                } else {
                    VesselAlertsModel!.removeAlert((<any>window)["appenv"].vesselIMONumber, getWRActionNeededAlertId(plan.scheduleId));
                }   
            })
        } catch (error) {
            console.warn("There has been a problem checking WR advice:", error);
        }
    }

    async checkIfPdfisAvailable(departureTerminalCode: string): Promise<string|null> {
        try {
            const response = await WeatherRoutingService.checkIfPdfisAvailable(departureTerminalCode);
            if(!response) {
                return null;
            }
            return response.id;

        } catch (error) {
            console.warn("There has been a problem with your fetch operation:", error);
            return null;
        }
    }

    async showWrPdf(wrId: string) {
        try {
            this.dispatchEvent(new CustomEvent('show-wni-pdf', { detail: { showAdvice:true ,wrId: wrId } }));
        } catch (error) {
            console.warn("There has been a problem with your fetch operation:", error);
            return null;
        }
    }

    async getWRProgress(auditId: string): Promise<any> {
        try {
            return await WeatherRoutingService.getWRProgress(auditId);
        } catch (error) {
            console.warn("There has been a problem with your fetch operation:", error);
            return null;
        }
    }

    async hasWRAdviseForSchedule(scheduleId: string): Promise<boolean> {
        try {
            return await WeatherRoutingService.hasWRAdviseForSchedule(scheduleId);
        } catch (error) {
            console.warn("Error checking if WR advise exists for schedule:", error);
            return false;
        }
    }
}
